import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalized,
  ITaskDenormalized,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from '../../../../../i18next';
import { Page } from '../../../../../layout/Page';
import { PageBody } from '../../../../../layout/PageBody';
import { useStandaloneAppContext } from '../../../../hooks/useStandaloneAppContext';
import {
  MessageInboxCurrent,
  MessageInboxPast
} from '../../components/MessageInbox';
import { ParticipantInfo } from '../../components/ParticipantInfo';
import { Task, TaskVariant } from '../../components/Task';
import { TopBanner } from '../../components/TopBanner';
import { getSubTaskContext } from '../../service';
import { useProductAnalytics } from '../../services/productAnalytics';

const Divider = styled('hr')`
  margin: ${(p) => p.theme.spacing(3)}px 0;
  border: 0;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: ${(p) => p.theme.custom.colors.gray.w300};
`;

const TaskListSectionContainer = styled('div')`
  margin-top: ${(p) => p.theme.spacing(4)}px;
  margin-bottom: ${(p) => p.theme.spacing(6)}px;

  :last-child {
    margin-bottom: ${(p) => p.theme.spacing(2)}px;
  }
`;

const TaskListSectionHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const TaskListSection = ({
  heading,
  tasks,
  d,
  variant
}: {
  heading?: string;
  tasks: ITaskDenormalized[];
  d: Doc<IExperienceDenormalized>;
  variant: TaskVariant;
}) => {
  const { trackEvent } = useProductAnalytics(d.id);
  const { routing } = useStandaloneAppContext();
  if (!tasks.length) {
    return null;
  }

  return (
    <TaskListSectionContainer>
      {heading && (
        <TaskListSectionHeading>
          <Typography variant="h4" component="h4">
            {heading}
          </Typography>
        </TaskListSectionHeading>
      )}
      <div>
        {tasks.map((t) => (
          <Task
            key={t.id}
            taskId={t.id}
            context={{
              completedAt: t.completedAt,
              completionCriteria: t.customCompletionCriteria,
              endDate: t.end,
              startDate: t.start,
              title: t.title,
              status: t.status,
              subtasks: t.blocks,

              org: d.data.org,
              user: d.data.user,
              userId: d.data.userId,

              headerLink: d.data.accessToken ? routing.toTask.href?.(d.data.accessToken, t.id) : undefined,
              headerOnClickHandler: () => {
                if (d.data.accessToken) {
                  routing.toTask.onClick(d.data.accessToken, t.id);
                }
              },

              subTaskContext: getSubTaskContext(t.id, d, trackEvent),
              additionalInfo: t.additionalInfo || null,
              customHints: d.data.customHints
            }}
            variant={variant}
          />
        ))}
      </div>
    </TaskListSectionContainer>
  );
};

const GroupedTaskList = ({ d }: { d: Doc<IExperienceDenormalized> }) => {
  const { t } = useTranslation();

  const groupedTasks = useMemo(() => {
    return sortBy(d.data.tasks, (task) => task.start?.toMillis() || 0).reduce<{
      current: ITaskDenormalized[];
      later: ITaskDenormalized[];
      done: ITaskDenormalized[];
    }>(
      (m, t) => {
        switch (t.status) {
          case TaskStatus.COMPLETE:
            m.done.push(t);
            break;
          case TaskStatus.NOT_STARTED:
            m.later.push(t);
            break;
          default:
            m.current.push(t);
            break;
        }
        return m;
      },
      {
        current: [],
        later: [],
        done: []
      }
    );
  }, [d]);
  return (
    <div>
      <TaskListSection tasks={groupedTasks.current} variant="expanded" d={d} />
      <TaskListSection
        heading={t('taskList.headings.later')}
        tasks={groupedTasks.later}
        variant="teaser"
        d={d}
      />
      <TaskListSection
        heading={t('taskList.headings.done')}
        tasks={groupedTasks.done}
        variant="normal"
        d={d}
      />
    </div>
  );
};

const PastInboxContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(4)
}));

export const StandaloneAppPageExperienceOverviewInner = ({
  experience,
  showTopBanner,
  isPreview
}: {
  experience: Doc<IExperienceDenormalized>;
  showTopBanner?: boolean;
  isPreview?: boolean;
}) => {
  const d = experience.data;
  const { trackEvent } = useProductAnalytics(d.accessToken);
  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
      name: 'Employee Instance Page Loaded',
      Explanation:
        'A participant or supporting actor loaded the page for a particular experience in the employee app'
    });
  }, [trackEvent]);

  return (
    <PageBody noTopPadding noSidePadding={isPreview}>
      <Page width="M">
        {showTopBanner && (
          <TopBanner
            d={{
              firstName: d.user.preferredName,
              salutation: d.salutation,
              actor: d.actor,
              orgName: d.org.name,
              title: d.title
            }}
          />
        )}
        {d.actor !== PARTICIPANT_ACTOR_KEY && (
          <>
            <ParticipantInfo d={d} />
          </>
        )}
        <MessageInboxCurrent ds={d.messages} />
        <Divider />
        <GroupedTaskList d={experience} />
        <PastInboxContainer>
          <MessageInboxPast ds={d.messages} />
        </PastInboxContainer>
      </Page>
    </PageBody>
  );
};
